import React, { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import useScanner from '../hooks/useScanner';
import '../styles/scanButton.css';

/**
 * A 44px scan button: starts the native scanner (the side key does the same). Renders nothing in a
 * browser or an app without a scanner. While scanning it reads "Scanning..." and a second tap stops
 * the scan. The scanned code is handled by the ScanListener (window.onBarcodeScanned), not here.
 */
const ScanButton = ({ className = '' }) => {
  const { available, scanning, start, stop, lastState } = useScanner();
  const startedHere = useRef(false);

  // Messages only for a scan THIS button started (several buttons can be on screen).
  useEffect(() => {
    if (!lastState || !startedHere.current) return;
    if (lastState.state === 'timeout') {
      toast.info('Nothing scanned');
      startedHere.current = false;
    } else if (lastState.state === 'error') {
      toast.error(`Scanner not available: ${lastState.message || (lastState.error !== undefined ? `error ${lastState.error}` : 'unknown error')}`);
      startedHere.current = false;
    } else if (lastState.state === 'idle' || lastState.state === 'cancelled') {
      startedHere.current = false;
    }
  }, [lastState]);

  if (!available) return null;

  const onClick = () => {
    if (scanning) {
      stop();
      return;
    }
    startedHere.current = true; // a failure to start arrives through lastState as an error
    start();
  };

  return (
    <button
      type="button"
      className={`scanBtn${scanning ? ' scanning' : ''} ${className}`.trim()}
      aria-label="Scan barcode"
      aria-pressed={scanning}
      title={scanning ? 'Scanning... tap to stop' : 'Scan barcode'}
      onClick={onClick}
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M7 8v8M10 8v8M13 8v8M16 8v8M18.5 8v8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
      {scanning && <span className="scanBtnLabel">Scanning...</span>}
    </button>
  );
};

export default ScanButton;
